// useDosage.ts
import { useState, useEffect } from 'react';
import { DosageEntry } from '../Types/Types';

const useDosage = () => {
  const [dosages, setDosages] = useState<DosageEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api/dosage`;

  const fetchDosages = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const lang = localStorage.getItem('lang') || 'en';
    const encodedSearchQuery = encodeURIComponent(searchQuery);

    const fullUrl = `${url}?lang=${lang}&search=${encodedSearchQuery}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch history');
      }

      const data: DosageEntry[] = await response.json();
      setDosages(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching history');
    } finally {
      setLoading(false);
    }
  };

  const createDosages = async (formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to create About Us entry.");
      }

      await fetchDosages();
    } catch (err: any) {
      setError(err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };





  const updateDosage = async (id: string, formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update product");
      }

      await fetchDosages(); // Refresh list to reflect updated product
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };


  const deleteDosage = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${url}/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete dosage');
      }

      setDosages(prev => prev.filter(d => d._id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting dosage');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDosages();
  }, [searchQuery, sortBy, sortOrder]);

  return {
    dosages,
    loading,
    error,
    createDosages,
    deleteDosage,
    updateDosage,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  };

};

export default useDosage;
